import React, { useCallback, useEffect, useState } from "react";
import api, { fileUrl , formatTanggal} from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import BackupExcelButton from "@/components/BackupExcelButton";
import { Paperclip, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const F_EMPTY = { tanggal: "", bulan: "", tahun: "", siswa: "", guru: "" };

export function BukuPenghubungKepsek() {
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => { try { const { data } = await api.get("/buku-penghubung"); setAll(data); } catch (e) { console.error(e); } }, []);
  useEffect(() => { load(); }, [load]);

  const items = all.filter((b) => {
    if (applied.tanggal && b.tanggal !== applied.tanggal) return false;
    if (applied.bulan && (b.tanggal || "").slice(5, 7) !== applied.bulan) return false;
    if (applied.tahun && (b.tanggal || "").slice(0, 4) !== applied.tahun) return false;
    if (applied.siswa && !(b.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    if (applied.guru && !(b.nama_guru || "").toLowerCase().includes(applied.guru.toLowerCase())) return false;
    return true;
  });

  const del = async (id) => {
    if (!window.confirm("Hapus catatan buku penghubung ini?")) return;
    try { await api.delete(`/buku-penghubung/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal hapus"); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/buku-penghubung/${editItem.id}`, {
        siswa_id: editItem.siswa_id,
        catatan_guru: editItem.catatan_guru,
        file_id: editItem.file_id || null,
        file_name: editItem.file_name || null,
      });
      toast.success("Catatan diperbarui");
      setEditItem(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle title="Buku Penghubung" action={<BackupExcelButton endpoint="buku-penghubung" filters={{ bulan: applied.bulan, tahun: applied.tahun, tanggal: applied.tanggal, nama_siswa: applied.siswa, nama_guru: applied.guru }} testId="backup-bp-btn" />} />
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="bp-filter"
        fields={[
          { key: "tanggal", type: "tanggal", label: "Tanggal" },
          { key: "bulan", type: "bulan", label: "Bulan" },
          { key: "tahun", type: "tahun", label: "Tahun" },
          { key: "siswa", type: "text", label: "Nama Siswa", placeholder: "Cari nama siswa" },
          { key: "guru", type: "text", label: "Nama Guru", placeholder: "Cari nama guru" },
        ]}
      />
      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Tanggal</th>
            <th className="text-left px-5 py-3">Nama Siswa</th>
            <th className="text-left px-5 py-3">Guru</th>
            <th className="text-left px-5 py-3">Catatan Guru</th>
            <th className="text-left px-5 py-3">Balasan Orangtua</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada catatan.</td></tr>}
            {items.map((b) => (
              <tr key={b.id} className="pp-row">
                <td className="px-5 py-3 font-mono text-xs">{formatTanggal(b.tanggal)}</td>
                <td className="px-5 py-3 font-medium">{b.nama_siswa}</td>
                <td className="px-5 py-3 text-slate-600">{b.nama_guru}</td>
                <td className="px-5 py-3 text-slate-700">
                  <div className="whitespace-pre-wrap">{b.catatan_guru}</div>
                  {b.file_id && (
                    <a href={fileUrl(b.file_id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 mt-1">
                      <Paperclip className="w-3 h-3" /> {b.file_name || "lampiran"}
                    </a>
                  )}
                </td>
                <td className="px-5 py-3 text-slate-700 italic">{b.balasan_ortu || <span className="text-slate-400">— belum dibalas —</span>}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditItem({ ...b })} className="pp-btn-ghost text-xs mr-1" data-testid={`bp-edit-${b.id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
                  <button onClick={() => del(b.id)} className="pp-btn-danger" data-testid={`bp-del-${b.id}`}><Trash2 className="w-3.5 h-3.5" />Hapus</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {editItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="bp-edit-modal">
          <div className="pp-card p-6 w-full max-w-lg animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Edit Catatan — {editItem.nama_siswa}</h3>
              <button onClick={() => setEditItem(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <form onSubmit={saveEdit} className="space-y-3">
              <div>
                <label className="pp-label">Catatan Guru</label>
                <textarea className="pp-input" rows={5} value={editItem.catatan_guru} onChange={(e) => setEditItem({ ...editItem, catatan_guru: e.target.value })} data-testid="bp-edit-catatan" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditItem(null)} className="pp-btn-secondary">Batal</button>
                <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="bp-edit-save">{saving ? "Menyimpan…" : "Simpan"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
